import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Bot, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

const INITIAL_MESSAGE: Message = {
  id: "welcome",
  role: "assistant",
  content: "👋 Welcome to SDC! I can help you with:\n\n• **Product information** & specs\n• **Fleet pricing** & quotes\n• **Service details** & capabilities\n• **Customization** options\n\nHow can I assist you today?",
};

const ChatbotWidget = () => {
  const location = useLocation();
  // Route-aware positioning for StickyQuoteCTA overlap prevention
  const hasStickyBar = ["/products", "/services"].some(p => location.pathname.startsWith(p)) && !location.pathname.includes("/customization");
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: "", email: "", phone: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { id: Date.now().toString(), role: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("chatbot", {
        body: {
          messages: [...messages.filter(m => m.id !== "welcome"), userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
        },
      });

      if (error) throw new Error(error.message);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: data?.reply || "I'm sorry, I couldn't process that. Please try again or contact us directly at info@sdcgulf.com.",
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // After 3 user messages, prompt for lead info if not submitted
      const userMsgCount = messages.filter(m => m.role === "user").length + 1;
      if (userMsgCount >= 3 && !leadSubmitted && !showLeadForm) {
        setTimeout(() => setShowLeadForm(true), 1000);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: (Date.now() + 1).toString(), role: "assistant", content: "I'm having trouble connecting right now. You can reach us at **info@sdcgulf.com** or call **+966 5 737 505 48**." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await supabase.functions.invoke("submit-contact-form", {
        body: {
          ...leadData,
          subject: "Chatbot Lead",
          message: `Lead captured from chatbot. Conversation topics: ${messages.filter(m => m.role === "user").map(m => m.content).join("; ")}`,
        },
      });
      setLeadSubmitted(true);
      setShowLeadForm(false);
      toast({ title: "Thanks!", description: "Our team will follow up shortly." });
    } catch {
      toast({ title: "Error", description: "Couldn't save your info. Try again.", variant: "destructive" });
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setIsOpen(true)}
            className={`fixed ${hasStickyBar ? "bottom-[7.5rem]" : "bottom-20"} right-4 md:bottom-24 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-primary to-amber-glow shadow-lg shadow-primary/30 flex items-center justify-center text-primary-foreground`}
            aria-label="Open chat"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed z-50 flex flex-col border border-border/50 bg-card shadow-2xl overflow-hidden inset-2 rounded-2xl md:inset-auto md:bottom-6 md:right-6 md:w-[360px] md:h-[500px] md:max-h-[calc(100vh-6rem)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-gradient-to-r from-primary/10 to-transparent">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center">
                  <Bot className="w-4 h-4 text-primary-foreground" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">SDC Assistant</p>
                  <p className="text-xs text-eco-green">Online</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && (
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-1">
                      <Bot className="w-3.5 h-3.5 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted/50 text-foreground rounded-bl-md"
                    }`}
                  >
                    {msg.content.split("\n").map((line, i) => (
                      <span key={i}>
                        {line.split(/(\*\*.*?\*\*)/).map((part, j) =>
                          part.startsWith("**") && part.endsWith("**") ? (
                            <strong key={j}>{part.slice(2, -2)}</strong>
                          ) : (
                            part
                          )
                        )}
                        {i < msg.content.split("\n").length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-6 h-6 rounded-full bg-eco-green/20 flex items-center justify-center shrink-0 mt-1">
                      <User className="w-3.5 h-3.5 text-eco-green" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="bg-muted/50 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" />
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0.1s" }} />
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0.2s" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Lead Form */}
            <AnimatePresence>
              {showLeadForm && !leadSubmitted && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-border/50">
                  <form onSubmit={handleLeadSubmit} className="p-3 bg-primary/5 space-y-2">
                    <p className="text-xs font-medium text-foreground">Want us to follow up? Leave your details:</p>
                    <Input placeholder="Name" value={leadData.name} onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} required className="h-8 text-xs bg-background/50" />
                    <Input type="email" placeholder="Email" value={leadData.email} onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} required className="h-8 text-xs bg-background/50" />
                    <Input type="tel" placeholder="Phone (optional)" value={leadData.phone} onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} className="h-8 text-xs bg-background/50" />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" className="flex-1 h-8 text-xs">Submit</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowLeadForm(false)}>Skip</Button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input */}
            <div className="p-3 border-t border-border/50">
              <form
                onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                className="flex gap-2"
              >
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-10 bg-background/50 border-border/50 text-sm"
                  disabled={isLoading}
                />
                <Button type="submit" size="sm" disabled={isLoading || !input.trim()} className="h-10 w-10 p-0">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;
